import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search,
  Eye,
  Filter,
  Wrench,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Laptop,
  Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatDate, formatCurrency } from '@/lib/utils';
import api from '@/services/api';
import CreateRepairModal from '../components/CreateRepairModal';

// Repair status color mapping
function getRepairStatusColor(status: string): string {
  const colors: Record<string, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    IN_TRANSIT: 'bg-blue-100 text-blue-800',
    AT_PROVIDER: 'bg-purple-100 text-purple-800',
    IN_REPAIR: 'bg-orange-100 text-orange-800',
    COMPLETED: 'bg-green-100 text-green-800',
    RETURNED: 'bg-green-100 text-green-800',
    WRITE_OFF: 'bg-red-100 text-red-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

function getRepairPriorityColor(priority: string): string {
  const colors: Record<string, string> = {
    LOW: 'bg-gray-100 text-gray-800',
    MEDIUM: 'bg-blue-100 text-blue-800',
    HIGH: 'bg-orange-100 text-orange-800',
    CRITICAL: 'bg-red-200 text-red-900',
  };
  return colors[priority] || 'bg-gray-100 text-gray-800';
}

function getRepairTypeColor(type: string): string {
  const colors: Record<string, string> = {
    CORRECTIVE: 'bg-red-50 text-red-700',
    PREVENTIVE: 'bg-blue-50 text-blue-700',
    WARRANTY: 'bg-green-50 text-green-700',
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
}

export default function RepairsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [priority, setPriority] = useState(searchParams.get('priority') || '');
  const [repairType, setRepairType] = useState(searchParams.get('repairType') || '');
  const [serviceProviderId, setServiceProviderId] = useState(searchParams.get('serviceProviderId') || '');
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1'));
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Handle action=new query parameter to open create modal
  useEffect(() => {
    if (searchParams.get('action') === 'new') {
      setShowCreateModal(true);
      // Remove action param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Fetch service providers for filter
  const { data: providersData } = useQuery({
    queryKey: ['serviceProviders'],
    queryFn: async () => {
      const response = await api.get('/service-providers?limit=100');
      return response.data.data || [];
    },
  });

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    if (search) params.set('search', search);
    if (status) params.set('status', status);
    if (priority) params.set('priority', priority);
    if (repairType) params.set('repairType', repairType);
    if (serviceProviderId) params.set('serviceProviderId', serviceProviderId);
    setSearchParams(params);
  }, [page, search, status, priority, repairType, serviceProviderId, setSearchParams]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['repairs', { page, search, status, priority, repairType, serviceProviderId }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (status) params.append('status', status);
      if (priority) params.append('priority', priority);
      if (repairType) params.append('repairType', repairType);
      if (serviceProviderId) params.append('serviceProviderId', serviceProviderId);
      
      const response = await api.get(`/repairs?${params.toString()}`);
      return response.data;
    },
  });

  const repairs = data?.data || [];
  const pagination = data?.pagination;
  const providers = providersData || [];

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setPriority('');
    setRepairType('');
    setServiceProviderId('');
    setPage(1);
  };

  const hasActiveFilters = search || status || priority || repairType || serviceProviderId;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Repairs</h1>
          <p className="text-muted-foreground">
            Manage asset repair records
            {pagination && ` • ${pagination.total} total`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => refetch()}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Repair
          </Button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Primary Filters Row */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by repair number, asset tag, or description..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[140px]"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="IN_TRANSIT">In Transit</option>
                <option value="AT_PROVIDER">At Provider</option>
                <option value="IN_REPAIR">In Repair</option>
                <option value="COMPLETED">Completed</option>
                <option value="RETURNED">Returned</option>
                <option value="WRITE_OFF">Write-off</option>
              </select>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[140px]"
                value={priority}
                onChange={(e) => {
                  setPriority(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Priorities</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
              <Button 
                variant={showFilters ? "secondary" : "outline"} 
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                More Filters
              </Button>
            </div>

            {/* Extended Filters Row */}
            {showFilters && (
              <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t">
                <select
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[160px]"
                  value={repairType}
                  onChange={(e) => {
                    setRepairType(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All Repair Types</option>
                  <option value="CORRECTIVE">Corrective</option>
                  <option value="PREVENTIVE">Preventive</option>
                  <option value="WARRANTY">Warranty</option>
                </select>
                <select
                  className="h-10 px-3 rounded-md border border-input bg-background text-sm min-w-[180px]"
                  value={serviceProviderId}
                  onChange={(e) => {
                    setServiceProviderId(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All Service Providers</option>
                  {providers.map((provider: any) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
                {hasActiveFilters && (
                  <Button variant="ghost" onClick={clearFilters}>
                    Clear All Filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Repairs Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : repairs.length === 0 ? (
            <div className="text-center py-12">
              <Wrench className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No repairs found</p>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters 
                  ? "Try adjusting your filters"
                  : "Create your first repair record to get started"}
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              ) : (
                <Button onClick={() => setShowCreateModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Repair
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Repair #</th>
                    <th className="text-left p-4 font-medium">Asset</th>
                    <th className="text-left p-4 font-medium">Employee</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Priority</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Service Provider</th>
                    <th className="text-left p-4 font-medium">Est. Cost</th>
                    <th className="text-left p-4 font-medium">Expected Return</th>
                    <th className="text-left p-4 font-medium">Loaner</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {repairs.map((repair: any) => (
                    <tr key={repair.id} className="hover:bg-muted/50 transition-colors">
                      <td className="p-4">
                        <Link 
                          to={`/repairs/${repair.id}`}
                          className="font-mono text-sm text-primary hover:underline"
                        >
                          {repair.repairNumber}
                        </Link>
                      </td>
                      <td className="p-4">
                        <div className="max-w-[180px]">
                          {repair.asset ? (
                            <>
                              <p className="font-medium text-sm">{repair.asset.assetTag}</p>
                              <p className="text-xs text-muted-foreground line-clamp-1">
                                {repair.asset.name}
                              </p>
                            </>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        {repair.employee ? (
                          <div>
                            <Link 
                              to={`/repairs/employee/${repair.employee.id}/history`}
                              className="font-medium text-sm hover:text-primary"
                            >
                              {repair.employee.firstName} {repair.employee.lastName}
                            </Link>
                            <p className="text-xs text-muted-foreground">
                              {repair.employee.employeeId}
                            </p>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          getRepairTypeColor(repair.repairType)
                        )}>
                          {repair.repairType}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          getRepairPriorityColor(repair.priority)
                        )}>
                          {repair.priority}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={cn(
                          "px-2 py-1 text-xs font-medium rounded-full",
                          getRepairStatusColor(repair.status)
                        )}>
                          {repair.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="p-4 text-sm">
                        {repair.serviceProvider?.name || '-'}
                      </td>
                      <td className="p-4 text-sm">
                        {repair.estimatedCost 
                          ? formatCurrency(repair.estimatedCost)
                          : '-'}
                      </td>
                      <td className="p-4 text-sm">
                        {repair.expectedReturnDate ? (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3 text-muted-foreground" />
                            {formatDate(repair.expectedReturnDate)}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="p-4">
                        {repair.loanerAsset ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Laptop className="w-3 h-3 text-blue-500" />
                            <span className="text-blue-600">{repair.loanerAsset.assetTag}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" asChild title="View Details">
                            <Link to={`/repairs/${repair.id}`}>
                              <Eye className="w-4 h-4" />
                            </Link>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} repairs
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className="w-8 h-8 p-0"
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create Repair Modal */}
      <CreateRepairModal 
        open={showCreateModal} 
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          refetch();
        }}
      />
    </div>
  );
}
